import { useMemo } from "react";
import { Avatar, Button, List, Card, Tag, Space, Divider } from "antd";
import { PlusOutlined, HistoryOutlined, ArrowUpOutlined, ArrowDownOutlined } from "@ant-design/icons";
import { uniqueBy } from "../../../../../variables/stringFunctions";

const frenchDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
    });
};

const SapTab = ({ sinistre, evaluations, onReevaluationClick }) => {
    const sinistreEvaluations = useMemo(() => {
        const filtered = evaluations?.filter((evaluation) => evaluation.encaissement !== true) || [];
        // Sort from newest to oldest (most recent first)
        return uniqueBy(filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)), "codeId");
    }, [evaluations]);

    const isFirstEvaluation = (index) => {
        // First evaluation is the oldest one, which is at the end of the list
        return index === sinistreEvaluations.length - 1;
    };

    const formatAmount = (amount) => {
        return new Intl.NumberFormat("fr-FR").format(Math.trunc(amount || 0));
    };

    const getDifference = (nouvelle, ancienne) => {
        const diff = nouvelle - ancienne;
        return {
            value: Math.abs(diff),
            isIncrease: diff > 0,
            percentage: ancienne ? ((diff / ancienne) * 100).toFixed(1) : 0
        };
    };

    return (
        <div style={{ padding: "24px", backgroundColor: "#f5f7fa", minHeight: "100%" }}>
            {/* Header Section */}
            <div style={{
                marginBottom: "24px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "16px"
            }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: "24px", fontWeight: 600, color: "#1a1a1a" }}>
                        <HistoryOutlined style={{ marginRight: "12px", color: '#242166' }} />
                        Historique des Réévaluations
                    </h2>
                    <p style={{ margin: "4px 0 0 0", color: "#6b7280", fontSize: "14px" }}>
                        {sinistreEvaluations.length} réévaluation(s) enregistrée(s)
                    </p>
                </div>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    size="large"
                    onClick={onReevaluationClick}
                    disabled={sinistre?.sortDossier === "T" || sinistre?.sortDossier === "S"}
                    style={{
                        backgroundColor: '#242166',
                        borderColor: "#000",
                        height: "44px",
                        borderRadius: "8px",
                        fontWeight: 500,
                        boxShadow: "0 2px 8px rgba(79, 70, 229, 0.3)"
                    }}
                >
                    Nouvelle Réévaluation
                </Button>
            </div>

            {/* Content Section */}
            {sinistreEvaluations.length ? (
                <List
                    dataSource={sinistreEvaluations}
                    renderItem={(item, index) => {
                        const diff = getDifference(item?.nouvelleProvision, item?.ancienneProvision);
                        const isLatest = index === 0; // Most recent is first
                        const isFirst = isFirstEvaluation(index);

                        return (
                            <Card
                                key={index}
                                style={{
                                    marginBottom: "16px",
                                    borderRadius: "12px",
                                    border: isFirst
                                        ? "2px solid #10b981"
                                        : isLatest
                                            ? "2px solid #4f46e5"
                                            : "1px solid #e5e7eb",
                                    boxShadow: isFirst
                                        ? "0 4px 20px rgba(16, 185, 129, 0.15)"
                                        : isLatest
                                            ? "0 4px 20px rgba(79, 70, 229, 0.15)"
                                            : "0 2px 8px rgba(0, 0, 0, 0.06)",
                                    overflow: "hidden",
                                    transition: "all 0.3s ease",
                                    background: isFirst ? "linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%)" : "#ffffff"
                                }}
                                hoverable
                            >
                                <div style={{ display: "flex", gap: "20px", alignItems: "flex-start" }}>
                                    {/* Avatar Section */}
                                    <Avatar
                                        size={64}
                                        src={`https://ui-avatars.com/api/?name=${isFirst ? "Ouverture" : item?.motifRevision || "R"}&background=${isFirst ? "10b981" : "4f46e5"}&color=fff&bold=true&size=128`}
                                        style={{ flexShrink: 0, border: isFirst ? "3px solid #d1fae5" : "3px solid #f3f4f6" }}
                                    />

                                    {/* Content Section */}
                                    <div style={{ flex: 1 }}>
                                        {/* Header with Tags */}
                                        <div style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "flex-start",
                                            marginBottom: "12px",
                                            flexWrap: "wrap",
                                            gap: "8px"
                                        }}>
                                            <div>
                                                <h3 style={{
                                                    margin: 0,
                                                    fontSize: "18px",
                                                    fontWeight: 600,
                                                    color: "#1a1a1a"
                                                }}>
                                                    {isFirst ? "Évaluation d'ouverture" : item?.motifRevision || "Réévaluation"}
                                                </h3>
                                                <p style={{
                                                    margin: "4px 0 0 0",
                                                    color: "#6b7280",
                                                    fontSize: "13px"
                                                }}>
                                                    {frenchDate(item?.createdAt)}
                                                </p>
                                            </div>
                                            <Space>
                                                {isFirst ? (
                                                    <Tag color="green" style={{
                                                        margin: 0,
                                                        padding: "4px 12px",
                                                        fontSize: "12px",
                                                        fontWeight: 500,
                                                        borderRadius: "6px"
                                                    }}>
                                                        Ouverture
                                                    </Tag>
                                                ) : isLatest && (
                                                    <Tag color="purple" style={{
                                                        margin: 0,
                                                        padding: "4px 12px",
                                                        fontSize: "12px",
                                                        fontWeight: 500,
                                                        borderRadius: "6px"
                                                    }}>
                                                        Courant
                                                    </Tag>
                                                )}
                                                {!isFirst && (
                                                    <Tag
                                                        color={diff.isIncrease ? "red" : "green"}
                                                        icon={diff.isIncrease ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                                                        style={{
                                                            margin: 0,
                                                            padding: "4px 12px",
                                                            fontSize: "12px",
                                                            fontWeight: 500,
                                                            borderRadius: "6px"
                                                        }}
                                                    >
                                                        {diff.isIncrease ? "+" : ""}{diff.percentage}%
                                                    </Tag>
                                                )}
                                            </Space>
                                        </div>

                                        <Divider style={{ margin: "12px 0" }} />

                                        {/* Provision Details */}
                                        <div style={{
                                            display: "grid",
                                            gridTemplateColumns: isFirst ? "1fr 1fr" : "repeat(auto-fit, minmax(250px, 1fr))",
                                            gap: "16px"
                                        }}>
                                            {/* Ancienne Provision - Hidden for first evaluation */}
                                            {!isFirst && (
                                                <div style={{
                                                    padding: "16px",
                                                    backgroundColor: "#fef3f2",
                                                    borderRadius: "8px",
                                                    border: "1px solid #fee2e2"
                                                }}>
                                                    <p style={{
                                                        margin: "0 0 4px 0",
                                                        fontSize: "12px",
                                                        color: "#991b1b",
                                                        fontWeight: 500,
                                                        textTransform: "uppercase",
                                                        letterSpacing: "0.5px"
                                                    }}>
                                                        Ancienne Provision
                                                    </p>
                                                    <p style={{
                                                        margin: 0,
                                                        fontSize: "22px",
                                                        fontWeight: 700,
                                                        color: "black"
                                                    }}>
                                                        {formatAmount(item?.ancienneProvision)} <span style={{ fontSize: "14px", fontWeight: 500 }}>FCFA</span>
                                                    </p>
                                                </div>
                                            )}

                                            {/* Nouvelle Provision */}
                                            <div style={{
                                                padding: "16px",
                                                backgroundColor: isFirst ? "#f0fdf4" : diff.isIncrease ? "#fef2f2" : "#f0fdf4",
                                                borderRadius: "8px",
                                                border: isFirst ? "1px solid #bbf7d0" : diff.isIncrease ? "1px solid #fee2e2" : "1px solid #bbf7d0"
                                            }}>
                                                <p style={{
                                                    margin: "0 0 4px 0",
                                                    fontSize: "12px",
                                                    color: isFirst ? "#166534" : diff.isIncrease ? "#991b1b" : "#166534",
                                                    fontWeight: 500,
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.5px"
                                                }}>
                                                    {isFirst ? "Provision Initiale" : isLatest ? "Evaluation Courante" : "Nouvelle Provision"}
                                                </p>
                                                <p style={{
                                                    margin: 0,
                                                    fontSize: "22px",
                                                    fontWeight: 700,
                                                    color: isFirst ? "black" : diff.isIncrease ? "black" : "black"
                                                }}>
                                                    {formatAmount(item?.nouvelleProvision)} <span style={{ fontSize: "14px", fontWeight: 500 }}>FCFA</span>
                                                </p>
                                            </div>

                                            {/* Date d'ouverture pour première évaluation */}
                                            {isFirst && (
                                                <div style={{
                                                    padding: "16px",
                                                    backgroundColor: "#eff6ff",
                                                    borderRadius: "8px",
                                                    border: "1px solid #dbeafe"
                                                }}>
                                                    <p style={{
                                                        margin: "0 0 4px 0",
                                                        fontSize: "12px",
                                                        color: "#1e40af",
                                                        fontWeight: 500,
                                                        textTransform: "uppercase",
                                                        letterSpacing: "0.5px"
                                                    }}>
                                                        Date d'Ouverture
                                                    </p>
                                                    <p style={{
                                                        margin: 0,
                                                        fontSize: "16px",
                                                        fontWeight: 600,
                                                        color: "#2563eb"
                                                    }}>
                                                        {frenchDate(item?.createdAt)}
                                                    </p>
                                                </div>
                                            )}

                                            {/* Différence - Hidden for first evaluation */}
                                            {!isFirst && (
                                                <div style={{
                                                    padding: "16px",
                                                    backgroundColor: "#f8fafc",
                                                    borderRadius: "8px",
                                                    border: "1px solid #e2e8f0"
                                                }}>
                                                    <p style={{
                                                        margin: "0 0 4px 0",
                                                        fontSize: "12px",
                                                        color: "#475569",
                                                        fontWeight: 500,
                                                        textTransform: "uppercase",
                                                        letterSpacing: "0.5px"
                                                    }}>
                                                        Différence
                                                    </p>
                                                    <p style={{
                                                        margin: 0,
                                                        fontSize: "22px",
                                                        fontWeight: 700,
                                                        color: diff.isIncrease ? "#dc2626" : "#16a34a"
                                                    }}>
                                                        {diff.isIncrease ? "+" : "-"}{formatAmount(diff.value)} <span style={{ fontSize: "14px", fontWeight: 500 }}>FCFA</span>
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        );
                    }}
                />
            ) : (
                <Card style={{
                    borderRadius: "12px",
                    textAlign: "center",
                    padding: "40px 20px",
                    border: "2px dashed #d1d5db"
                }}>
                    <HistoryOutlined style={{ fontSize: "64px", color: "#d1d5db", marginBottom: "16px" }} />
                    <h3 style={{ fontSize: "18px", fontWeight: 600, color: "#4b5563", margin: "0 0 8px 0" }}>
                        Aucune réévaluation effectuée
                    </h3>
                    <p style={{ color: "#6b7280", margin: 0 }}>
                        Ce dossier sinistre n'a pas encore été réévalué
                    </p>
                </Card>
            )}
        </div>
    );
};

export default SapTab;